import React, { useState, useEffect } from 'react';
import { 
  Calculator, 
  Download, 
  Share2, 
  Info, 
  TrendingUp, 
  DollarSign,
  Calendar,
  PiggyBank,
  Target,
  BarChart3
} from 'lucide-react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useScrollToTopOnMount } from '../hooks/useScrollToTop';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface CalculationResult {
  totalNeeded: number;
  monthlySavingsRequired: number;
  futureValueOfCurrentSavings: number;
  yearsToRetirement: number;
  totalContributions: number;
  totalInterest: number;
  chartData: {
    labels: string[];
    datasets: any[];
  };
}

const RetirementCalculator = () => {
  const [inputs, setInputs] = useState({
    currentAge: 30,
    retirementAge: 65,
    currentSavings: 50000,
    monthlyContribution: 1000,
    monthlyExpensesPostRetirement: 8000,
    annualReturnBeforeRetirement: 8,
    annualReturnAfterRetirement: 5,
    yearsInRetirement: 25
  });

  const [result, setResult] = useState<CalculationResult | null>(null);
  const [showTooltip, setShowTooltip] = useState<string | null>(null);

  // Scroll to top when component mounts
  useScrollToTopOnMount();

  // Update document title and meta description
  useEffect(() => {
    document.title = 'Retirement Planning Calculator – Plan Your Financial Future | Simran Industries';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Use our free retirement planning calculator to estimate how much you need to save monthly for a comfortable retirement. Plan your financial future today.');
    }

    // Add schema markup
    const schema = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "Retirement Planning Calculator",
      "description": "Free online retirement planning calculator to estimate monthly savings needed for retirement goals",
      "url": "https://simranindustries.com/calculators/retirement-planning-calculator",
      "applicationCategory": "FinanceApplication",
      "operatingSystem": "Web Browser",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "INR"
      },
      "mainEntity": {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "How much should I save for retirement?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "The amount you need to save depends on your current age, retirement age, expected expenses, and investment returns. Our calculator helps you determine the monthly savings required to meet your retirement goals."
            }
          },
          {
            "@type": "Question",
            "name": "What is compound interest?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Compound interest is when your investment earnings also earn returns over time. This creates exponential growth of your savings, making early retirement planning crucial for maximizing your retirement corpus."
            }
          },
          {
            "@type": "Question",
            "name": "How do I calculate retirement corpus?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Our calculator considers your monthly expenses after retirement, expected investment returns, and years in retirement to determine the total corpus needed. It then calculates how much you need to save monthly to reach this target."
            }
          }
        ]
      }
    };

    // Remove existing schema if any
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) {
      existingSchema.remove();
    }

    // Add new schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    // Cleanup on unmount
    return () => {
      const scriptToRemove = document.querySelector('script[type="application/ld+json"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  const calculateRetirement = () => {
    const {
      currentAge,
      retirementAge,
      currentSavings,
      monthlyContribution,
      monthlyExpensesPostRetirement,
      annualReturnBeforeRetirement,
      annualReturnAfterRetirement,
      yearsInRetirement
    } = inputs;

    const yearsToRetirement = retirementAge - currentAge;
    const monthlyReturnBefore = annualReturnBeforeRetirement / 100 / 12;
    const monthlyReturnAfter = annualReturnAfterRetirement / 100 / 12;

    // 1. Calculate future value of current savings + monthly contributions
    const futureValueOfCurrentSavings = currentSavings * Math.pow(1 + monthlyReturnBefore, yearsToRetirement * 12) +
      monthlyContribution * ((Math.pow(1 + monthlyReturnBefore, yearsToRetirement * 12) - 1) / monthlyReturnBefore);

    // 2. Calculate total retirement corpus needed
    const totalRetirementCorpus = monthlyExpensesPostRetirement * 12 * 
      ((1 - Math.pow(1 + annualReturnAfterRetirement / 100, -yearsInRetirement)) / (annualReturnAfterRetirement / 100));

    // 3. Calculate additional monthly savings needed
    const additionalMonthlySavings = Math.max(0, (totalRetirementCorpus - futureValueOfCurrentSavings) / 
      ((Math.pow(1 + monthlyReturnBefore, yearsToRetirement * 12) - 1) / monthlyReturnBefore));

    // Generate chart data
    const chartLabels = [];
    const savingsData = [];
    const targetData = [];
    
    for (let year = 0; year <= yearsToRetirement; year++) {
      chartLabels.push(`Age ${currentAge + year}`);
      
      // Current savings growth
      const currentSavingsValue = currentSavings * Math.pow(1 + annualReturnBeforeRetirement / 100, year);
      const contributionsValue = monthlyContribution * 12 * year;
      const totalSavings = currentSavingsValue + contributionsValue;
      savingsData.push(Math.round(totalSavings));
      
      // Target corpus (linear growth for visualization)
      const targetCorpus = (totalRetirementCorpus / yearsToRetirement) * year;
      targetData.push(Math.round(targetCorpus));
    }

    const chartData = {
      labels: chartLabels,
      datasets: [
        {
          label: 'Projected Savings',
          data: savingsData,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4,
        },
        {
          label: 'Target Corpus',
          data: targetData,
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          borderDash: [5, 5],
          tension: 0.4,
        },
      ],
    };

    const totalContributions = monthlyContribution * 12 * yearsToRetirement;
    const totalInterest = futureValueOfCurrentSavings - currentSavings - totalContributions;

    setResult({
      totalNeeded: Math.round(totalRetirementCorpus),
      monthlySavingsRequired: Math.round(additionalMonthlySavings),
      futureValueOfCurrentSavings: Math.round(futureValueOfCurrentSavings),
      yearsToRetirement,
      totalContributions: Math.round(totalContributions),
      totalInterest: Math.round(totalInterest),
      chartData
    });
  };

  useEffect(() => {
    calculateRetirement();
  }, [inputs]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleInputChange = (field: string, value: number) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const downloadPDF = () => {
    if (!result) return;
    
    const content = `
Retirement Planning Calculator Results

Current Age: ${inputs.currentAge} years
Retirement Age: ${inputs.retirementAge} years
Current Savings: ${formatCurrency(inputs.currentSavings)}
Monthly Contribution: ${formatCurrency(inputs.monthlyContribution)}
Monthly Expenses Post-Retirement: ${formatCurrency(inputs.monthlyExpensesPostRetirement)}

RESULTS:
Total Corpus Needed: ${formatCurrency(result.totalNeeded)}
Monthly Savings Required: ${formatCurrency(result.monthlySavingsRequired)}
Future Value of Current Savings: ${formatCurrency(result.futureValueOfCurrentSavings)}
Years to Retirement: ${result.yearsToRetirement}

Generated on: ${new Date().toLocaleDateString()}
    `;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'retirement-planning-results.txt';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const shareResults = () => {
    if (!result) return;
    
    const shareText = `My retirement planning results:
- Total needed: ${formatCurrency(result.totalNeeded)}
- Monthly savings required: ${formatCurrency(result.monthlySavingsRequired)}
- Years to retirement: ${result.yearsToRetirement}`;
    
    if (navigator.share) {
      navigator.share({
        title: 'Retirement Planning Results',
        text: shareText,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(shareText);
      alert('Results copied to clipboard!');
    }
  };

  const tooltips = {
    currentAge: "Your current age in years",
    retirementAge: "The age at which you plan to retire",
    currentSavings: "Total amount you have saved so far",
    monthlyContribution: "Amount you currently save each month (optional)",
    monthlyExpensesPostRetirement: "Expected monthly expenses after retirement",
    annualReturnBeforeRetirement: "Expected annual return on investments before retirement",
    annualReturnAfterRetirement: "Expected annual return on investments after retirement",
    yearsInRetirement: "Number of years you expect to live after retirement"
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4">
              <Calculator className="h-8 w-8 text-amber-600 mr-3" />
              <h1 className="text-4xl font-bold text-gray-900">Retirement Planning Calculator</h1>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Plan your financial future with our comprehensive retirement calculator. 
              Estimate how much you need to save monthly to achieve your retirement goals.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Input Section */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Target className="h-6 w-6 text-amber-600 mr-2" />
                Your Information
              </h2>

              <div className="space-y-6">
                {/* Age Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Current Age (years)
                      <button
                        onMouseEnter={() => setShowTooltip('currentAge')}
                        onMouseLeave={() => setShowTooltip(null)}
                        className="ml-1 text-gray-400 hover:text-gray-600"
                      >
                        <Info size={14} />
                      </button>
                    </label>
                    <input
                      type="number"
                      value={inputs.currentAge}
                      onChange={(e) => handleInputChange('currentAge', Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500"
                      min="18"
                      max="80"
                    />
                    {showTooltip === 'currentAge' && (
                      <div className="absolute z-10 bg-gray-900 text-white text-xs rounded py-1 px-2 mt-1">
                        {tooltips.currentAge}
                      </div>
                    )}
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Retirement Age (years)
                      <button
                        onMouseEnter={() => setShowTooltip('retirementAge')}
                        onMouseLeave={() => setShowTooltip(null)}
                        className="ml-1 text-gray-400 hover:text-gray-600"
                      >
                        <Info size={14} />
                      </button>
                    </label>
                    <input
                      type="number"
                      value={inputs.retirementAge}
                      onChange={(e) => handleInputChange('retirementAge', Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500"
                      min={inputs.currentAge + 1}
                      max="80"
                    />
                    {showTooltip === 'retirementAge' && (
                      <div className="absolute z-10 bg-gray-900 text-white text-xs rounded py-1 px-2 mt-1">
                        {tooltips.retirementAge}
                      </div>
                    )}
                  </div>
                </div>

                {/* Savings Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Current Savings (₹)
                      <button
                        onMouseEnter={() => setShowTooltip('currentSavings')}
                        onMouseLeave={() => setShowTooltip(null)}
                        className="ml-1 text-gray-400 hover:text-gray-600"
                      >
                        <Info size={14} />
                      </button>
                    </label>
                    <input
                      type="number"
                      value={inputs.currentSavings}
                      onChange={(e) => handleInputChange('currentSavings', Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500"
                      min="0"
                      step="1000"
                    />
                    {showTooltip === 'currentSavings' && (
                      <div className="absolute z-10 bg-gray-900 text-white text-xs rounded py-1 px-2 mt-1">
                        {tooltips.currentSavings}
                      </div>
                    )}
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Monthly Contribution (₹)
                      <button
                        onMouseEnter={() => setShowTooltip('monthlyContribution')}
                        onMouseLeave={() => setShowTooltip(null)}
                        className="ml-1 text-gray-400 hover:text-gray-600"
                      >
                        <Info size={14} />
                      </button>
                    </label>
                    <input
                      type="number"
                      value={inputs.monthlyContribution}
                      onChange={(e) => handleInputChange('monthlyContribution', Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500"
                      min="0"
                      step="100"
                    />
                    {showTooltip === 'monthlyContribution' && (
                      <div className="absolute z-10 bg-gray-900 text-white text-xs rounded py-1 px-2 mt-1">
                        {tooltips.monthlyContribution}
                      </div>
                    )}
                  </div>
                </div>

                {/* Expenses */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Monthly Expenses Post-Retirement (₹)
                    <button
                      onMouseEnter={() => setShowTooltip('monthlyExpensesPostRetirement')}
                      onMouseLeave={() => setShowTooltip(null)}
                      className="ml-1 text-gray-400 hover:text-gray-600"
                    >
                      <Info size={14} />
                    </button>
                  </label>
                  <input
                    type="number"
                    value={inputs.monthlyExpensesPostRetirement}
                    onChange={(e) => handleInputChange('monthlyExpensesPostRetirement', Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500"
                    min="0"
                    step="1000"
                  />
                  {showTooltip === 'monthlyExpensesPostRetirement' && (
                    <div className="absolute z-10 bg-gray-900 text-white text-xs rounded py-1 px-2 mt-1">
                      {tooltips.monthlyExpensesPostRetirement}
                    </div>
                  )}
                </div>

                {/* Returns */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Annual Return Before Retirement (%)
                      <button
                        onMouseEnter={() => setShowTooltip('annualReturnBeforeRetirement')}
                        onMouseLeave={() => setShowTooltip(null)}
                        className="ml-1 text-gray-400 hover:text-gray-600"
                      >
                        <Info size={14} />
                      </button>
                    </label>
                    <input
                      type="number"
                      value={inputs.annualReturnBeforeRetirement}
                      onChange={(e) => handleInputChange('annualReturnBeforeRetirement', Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500"
                      min="0"
                      max="20"
                      step="0.1"
                    />
                    {showTooltip === 'annualReturnBeforeRetirement' && (
                      <div className="absolute z-10 bg-gray-900 text-white text-xs rounded py-1 px-2 mt-1">
                        {tooltips.annualReturnBeforeRetirement}
                      </div>
                    )}
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Annual Return After Retirement (%)
                      <button
                        onMouseEnter={() => setShowTooltip('annualReturnAfterRetirement')}
                        onMouseLeave={() => setShowTooltip(null)}
                        className="ml-1 text-gray-400 hover:text-gray-600"
                      >
                        <Info size={14} />
                      </button>
                    </label>
                    <input
                      type="number"
                      value={inputs.annualReturnAfterRetirement}
                      onChange={(e) => handleInputChange('annualReturnAfterRetirement', Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500"
                      min="0"
                      max="20"
                      step="0.1"
                    />
                    {showTooltip === 'annualReturnAfterRetirement' && (
                      <div className="absolute z-10 bg-gray-900 text-white text-xs rounded py-1 px-2 mt-1">
                        {tooltips.annualReturnAfterRetirement}
                      </div>
                    )}
                  </div>
                </div>

                {/* Years in Retirement */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Years in Retirement
                    <button
                      onMouseEnter={() => setShowTooltip('yearsInRetirement')}
                      onMouseLeave={() => setShowTooltip(null)}
                      className="ml-1 text-gray-400 hover:text-gray-600"
                    >
                      <Info size={14} />
                    </button>
                  </label>
                  <input
                    type="number"
                    value={inputs.yearsInRetirement}
                    onChange={(e) => handleInputChange('yearsInRetirement', Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500"
                    min="10"
                    max="50"
                  />
                  {showTooltip === 'yearsInRetirement' && (
                    <div className="absolute z-10 bg-gray-900 text-white text-xs rounded py-1 px-2 mt-1">
                      {tooltips.yearsInRetirement}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Results Section */}
            <div className="space-y-6">
              {result && (
                <>
                  {/* Main Results */}
                  <div className="bg-white rounded-lg shadow-lg p-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                      <DollarSign className="h-6 w-6 text-green-600 mr-2" />
                      Your Results
                    </h2>

                    <div className="space-y-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-green-800 mb-2">Total Corpus Needed</h3>
                        <p className="text-3xl font-bold text-green-600">{formatCurrency(result.totalNeeded)}</p>
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-blue-800 mb-2">Monthly Savings Required</h3>
                        <p className="text-3xl font-bold text-blue-600">{formatCurrency(result.monthlySavingsRequired)}</p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-gray-600 mb-1">Future Value of Current Savings</h4>
                          <p className="text-lg font-semibold text-gray-900">{formatCurrency(result.futureValueOfCurrentSavings)}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-gray-600 mb-1">Years to Retirement</h4>
                          <p className="text-lg font-semibold text-gray-900">{result.yearsToRetirement} years</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-gray-600 mb-1">Total Contributions</h4>
                          <p className="text-lg font-semibold text-gray-900">{formatCurrency(result.totalContributions)}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-gray-600 mb-1">Total Interest Earned</h4>
                          <p className="text-lg font-semibold text-gray-900">{formatCurrency(result.totalInterest)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 mt-6">
                      <button
                        onClick={downloadPDF}
                        className="flex items-center justify-center px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors"
                      >
                        <Download size={16} className="mr-2" />
                        Download Results
                      </button>
                      <button
                        onClick={shareResults}
                        className="flex items-center justify-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                      >
                        <Share2 size={16} className="mr-2" />
                        Share Results
                      </button>
                    </div>
                  </div>

                  {/* Chart */}
                  <div className="bg-white rounded-lg shadow-lg p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                      <BarChart3 className="h-5 w-5 text-amber-600 mr-2" />
                      Savings Growth Projection
                    </h3>
                    <div className="h-64">
                      <Line
                        data={result.chartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              position: 'top' as const,
                            },
                            title: {
                              display: true,
                              text: 'Savings Growth vs Target Corpus',
                            },
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              ticks: {
                                callback: function(value: any) {
                                  return formatCurrency(Number(value));
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="mt-12 bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Info className="h-6 w-6 text-blue-600 mr-2" />
              How It Works
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Calculation Method</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2">•</span>
                    <span>Calculates future value of current savings using compound interest</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2">•</span>
                    <span>Determines total retirement corpus needed based on monthly expenses</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2">•</span>
                    <span>Calculates additional monthly savings required to meet the target</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2">•</span>
                    <span>Accounts for different returns before and after retirement</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Important Notes</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>This is an estimate and actual returns may vary</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Consider inflation and changing expenses over time</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Consult a financial advisor for personalized advice</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Regularly review and adjust your retirement plan</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default RetirementCalculator; 